import { Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { ChatMarkdown } from '@/modules/common/Chat/components/ChatMarkdown';
import { getInteractiveMessageInputSx } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.styles';
import { getInteractiveMessageResponse } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import {
  InteractiveMessageActionConfig,
  InteractiveMessageCard,
} from '@/modules/household/chat/components/messages/interactive/InteractiveMessageCard';
import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import { COLORS } from '@/styles/tokens/colors';
import { MessageResponseAction, MessageStatus } from '@/types/schema';

interface GroupedQuestionAnswer {
  id: string;
  answer: string;
}

const GROUPED_QUESTION_ACTIONS: Record<
  MessageResponseAction,
  InteractiveMessageActionConfig
> = {
  [MessageResponseAction.Dismiss]: {
    label: 'Skip all',
    variant: 'secondary',
  },
  [MessageResponseAction.Submit]: { label: 'Submit', variant: 'primary' },
};

const GROUPED_QUESTION_RESPONDED_VERBS: Record<MessageResponseAction, string> =
  {
    [MessageResponseAction.Submit]: 'answered these questions',
    [MessageResponseAction.Dismiss]: 'skipped these questions',
  };

export function parseGroupedQuestionAnswers(
  content: string
): Map<string, string> {
  try {
    const parsed: unknown = JSON.parse(content);
    if (!Array.isArray(parsed)) return new Map();

    return new Map(
      parsed.flatMap((answer): [string, string][] => {
        if (
          typeof answer === 'object' &&
          answer !== null &&
          'id' in answer &&
          typeof answer.id === 'string' &&
          'answer' in answer &&
          typeof answer.answer === 'string'
        ) {
          return [[answer.id, answer.answer]];
        }
        return [];
      })
    );
  } catch {
    return new Map();
  }
}

/** Renders validation questions as one card and submits the answered subset. */
export function GroupedQuestionMessageRenderer({
  message,
}: {
  message: HouseholdChatMessage;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const { respondToMessage, respondingToMessage } = useHouseholdChatContext();
  const content = message.groupedQuestionContent;
  const response = getInteractiveMessageResponse(message);
  const submittedAnswers = response
    ? parseGroupedQuestionAnswers(response.content)
    : new Map();
  const isSubmitting = respondingToMessage?.messageId === message.id;

  if (!content) {
    return <ChatMarkdown markdown={message.content} />;
  }

  const questionAnswers: GroupedQuestionAnswer[] = content.questions.flatMap(
    (question) => {
      const answer = answers[question.id]?.trim();
      return answer ? [{ id: question.id, answer }] : [];
    }
  );
  const hasAnswers = questionAnswers.length > 0;

  const handleRespond = (action: MessageResponseAction) => {
    const input = { messageID: message.id, responseAction: action };
    if (action === MessageResponseAction.Submit) {
      void respondToMessage({ ...input, questionAnswers });
      return;
    }
    void respondToMessage(input);
  };

  return (
    <InteractiveMessageCard
      message={message}
      actions={GROUPED_QUESTION_ACTIONS}
      hiddenActions={{
        [MessageResponseAction.Dismiss]: hasAnswers,
        [MessageResponseAction.Submit]: !hasAnswers,
      }}
      onRespond={handleRespond}
      respondedVerbs={GROUPED_QUESTION_RESPONDED_VERBS}
    >
      <Stack gap={2}>
        {content.questions.map((question) => (
          <Stack key={question.id} gap={0.5}>
            <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
              {question.title}
            </Typography>
            {question.subtitle && (
              <Typography variant="body-xs">{question.subtitle}</Typography>
            )}
            {message.status === MessageStatus.Pending ? (
              <TextInput
                id={`grouped-question-${message.id}-${question.id}`}
                label={`Response to ${question.title}`}
                hideLabel
                inputProps={{ 'aria-label': `Response to ${question.title}` }}
                placeholder="Type your response..."
                value={answers[question.id] ?? ''}
                multiline
                minRows={2}
                disabled={isSubmitting}
                onChange={(event) =>
                  setAnswers((current) => ({
                    ...current,
                    [question.id]: event.target.value,
                  }))
                }
                sx={getInteractiveMessageInputSx(COLORS.GRAY[200])}
              />
            ) : (
              message.status === MessageStatus.Responded &&
              response && (
                <Typography
                  variant="title-xs"
                  sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
                >
                  {submittedAnswers.get(question.id) ?? 'Skipped'}
                </Typography>
              )
            )}
          </Stack>
        ))}
      </Stack>
    </InteractiveMessageCard>
  );
}
